/**
 * The sales-experience writes and reads, kept free of React so they can be tested.
 *
 *   - "Accept matched items" (`POST /cases/{id}/lines/accept-matched`): one write that
 *     records Confirm on every matched line nobody decided yet.
 *   - Bulk status and Take (`POST /work/bulk/status`, `POST /work/bulk/assign`): each case
 *     is its own write with the same rules as on the case page.
 *   - Case activity (`POST /cases/{id}/activity`): what the effort measurement counts
 *     (opened, left, copied, next case). Read only in aggregate, never per person.
 *
 * Every write reaches the app database only. Nothing is sent to a customer and nothing
 * is written to F&O.
 *
 * A queue row does not carry the version of its work row, so a bulk change first sends
 * version 0 (right for every case nobody touched). A case that answers "changed" is read
 * once: when it still stands as the row showed it (same status, same assignee), it is
 * sent again with its current version; otherwise it is reported as changed by a colleague.
 */

import { logger } from "@/lib/logger";

import {
  acceptMatchedLines,
  type AcceptMatchedResult,
  bulkAssignCases,
  type BulkCaseRef,
  type BulkItemResult,
  type BulkResult,
  bulkSetCaseStatus,
  type CaseActivityRequest,
  getCase,
  recordCaseActivity,
} from "./generated";
import type { CaseWork, LineDecision, LiveQuoteCaseDetail, WorkStatus } from "./live-types";

/** The most cases one bulk request may carry (the server refuses more). */
export const BULK_CHUNK = 100;

/**
 * Record Confirm on the matched, undecided lines (`line_keys` null = all of them). With
 * `includeSuggested` the suggested lines that have an item are confirmed too: the one
 * click of a person on "Confirm n items" (ease round, 13 Oct 2026). The flag is sent only
 * when it is set, so "Accept matched items" sends what it always sent.
 */
export async function postAcceptMatched(
  caseId: string,
  expectedVersion: number,
  lineKeys: string[] | null = null,
  includeSuggested = false,
): Promise<AcceptMatchedResult | undefined> {
  const { data } = await acceptMatchedLines({
    path: { case_id: caseId },
    body: {
      expected_version: expectedVersion,
      line_keys: lineKeys,
      ...(includeSuggested ? { include_suggested: true } : {}),
    },
    throwOnError: true,
  });
  return data;
}

/** The cached case with the answer of "Accept matched items" applied to its lines. */
export function applyAcceptResult<
  T extends {
    work?: CaseWork;
    work_status?: string;
    lines?: { line_key?: string; decision?: unknown }[];
  },
>(detail: T | undefined, result: AcceptMatchedResult | undefined): T | undefined {
  if (!detail || !result) {
    return detail;
  }
  const byKey = new Map<string, LineDecision>(
    (result.accepted ?? []).map((decision) => [decision.line_key, decision as LineDecision]),
  );
  const lines = detail.lines?.map((line) =>
    line.line_key && byKey.has(line.line_key)
      ? { ...line, decision: byKey.get(line.line_key) }
      : line,
  );
  return {
    ...detail,
    work: result.work as CaseWork,
    work_status: result.work_status,
    lines,
  };
}

// ─── bulk ─────────────────────────────────────────────────────────────────────

/** What the queue showed for a selected row: the work status and the assignee. */
export interface BulkRow {
  id: string;
  workStatus?: WorkStatus | string | null;
  assignee?: string | null;
}

/** One status for all, or Take / Release for all. */
export type BulkRequest =
  | { kind: "status"; status: "done" | "not_for_us" | "waiting_customer" | "open"; note?: string }
  | { kind: "assign"; action: "take" | "release" };

/** Sends one chunk of at most {@link BULK_CHUNK} cases. */
export type BulkSend = (cases: BulkCaseRef[]) => Promise<BulkResult>;

/** Reads the current work row of one case; null when it cannot be read. */
export type ReadWork = (
  caseId: string,
) => Promise<{ work: CaseWork | null; workStatus: string | null } | null>;

function same(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();
}

function chunks<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let start = 0; start < items.length; start += size) {
    out.push(items.slice(start, start + size));
  }
  return out;
}

async function sendAll(cases: BulkCaseRef[], send: BulkSend): Promise<BulkItemResult[]> {
  const results: BulkItemResult[] = [];
  for (const chunk of chunks(cases, BULK_CHUNK)) {
    const answer = await send(chunk);
    results.push(...(answer.results ?? []));
  }
  return results;
}

/**
 * Run one bulk change over the selected rows (see the file comment for the versions).
 * `known` gives a version already on screen (a cached case), so no read is needed.
 */
export async function runBulk(
  rows: readonly BulkRow[],
  send: BulkSend,
  readWork: ReadWork,
  known: (caseId: string) => number | null = () => null,
): Promise<BulkResult> {
  const unique = [...new Map(rows.map((row) => [row.id, row])).values()];
  const first = await sendAll(
    unique.map((row) => ({ case_id: row.id, expected_version: known(row.id) ?? 0 })),
    send,
  );
  const byId = new Map(first.map((entry) => [entry.case_id, entry]));
  const retry: BulkCaseRef[] = [];
  for (const entry of first) {
    if (entry.code !== "version_conflict") {
      continue;
    }
    const row = unique.find((candidate) => candidate.id === entry.case_id);
    const now = row ? await readWork(row.id) : null;
    // Retry only when the case still stands as the row showed it.
    if (
      row &&
      now &&
      same(now.workStatus, row.workStatus ?? "open") &&
      same(now.work?.assignee, row.assignee)
    ) {
      retry.push({ case_id: row.id, expected_version: now.work?.version ?? 0 });
    }
  }
  if (retry.length > 0) {
    logger.debug("[queue] bulk: send again with current versions", { cases: retry.length });
    for (const entry of await sendAll(retry, send)) {
      byId.set(entry.case_id, entry);
    }
  }
  const results = unique.map(
    (row) => byId.get(row.id) ?? ({ case_id: row.id, code: "error" } as BulkItemResult),
  );
  const ok = results.filter((entry) => entry.code === "ok").length;
  return { results, ok_count: ok, failed_count: results.length - ok };
}

/** The sender of one bulk request. */
export function bulkSender(request: BulkRequest): BulkSend {
  return async (cases) => {
    if (request.kind === "status") {
      const { data } = await bulkSetCaseStatus({
        body: { cases, status: request.status, note: request.note ?? null },
        throwOnError: true,
      });
      return data ?? { results: [] };
    }
    const { data } = await bulkAssignCases({
      body: { cases, action: request.action },
      throwOnError: true,
    });
    return data ?? { results: [] };
  };
}

/** Read the work row of one case from `GET /cases/{id}` (only for a case that changed). */
export const readCaseWork: ReadWork = async (caseId) => {
  try {
    const { data } = await getCase({ path: { case_id: caseId }, throwOnError: true });
    const detail = data as LiveQuoteCaseDetail | undefined;
    return detail ? { work: detail.work ?? null, workStatus: detail.work_status ?? "open" } : null;
  } catch (error) {
    logger.warn("[queue] bulk: case could not be read", { caseId, error });
    return null;
  }
};

/**
 * One write from a queue row, which does not know its version: version 0 first; on a
 * version conflict the case is read once and sent again only while `stillAsShown` holds.
 */
export async function sendWithRowVersion<T>(
  row: BulkRow,
  send: (version: number) => Promise<T>,
  stillAsShown: (work: CaseWork | null) => boolean = () => true,
  readWork: ReadWork = readCaseWork,
): Promise<T> {
  try {
    return await send(0);
  } catch (error) {
    const { status, body } = (error ?? {}) as { status?: number; body?: unknown };
    const code = (body as { detail?: { code?: unknown } } | null)?.detail?.code;
    if (status !== 409 || code !== "version_conflict") {
      throw error;
    }
    const now = await readWork(row.id);
    if (
      !now ||
      !same(now.workStatus, row.workStatus ?? "open") ||
      !same(now.work?.assignee, row.assignee) ||
      !stillAsShown(now.work)
    ) {
      throw error;
    }
    return send(now.work?.version ?? 0);
  }
}

// ─── activity ─────────────────────────────────────────────────────────────────

const ACTIVITY_URL = (caseId: string) => `/api/cases/${encodeURIComponent(caseId)}/activity`;

/**
 * Record one activity. Never throws: a lost measurement must not disturb the work.
 * `leaving` sends it with `navigator.sendBeacon` (falling back to a keepalive fetch),
 * so it still arrives while the page closes.
 */
export async function sendCaseActivity(
  caseId: string,
  body: CaseActivityRequest,
  leaving = false,
): Promise<void> {
  if (!caseId.trim()) {
    return;
  }
  try {
    if (leaving) {
      const payload = JSON.stringify(body);
      const beacon =
        typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
          ? navigator.sendBeacon(
              ACTIVITY_URL(caseId),
              new Blob([payload], { type: "application/json" }),
            )
          : false;
      if (!beacon && typeof fetch === "function") {
        await fetch(ACTIVITY_URL(caseId), {
          method: "POST",
          body: payload,
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          keepalive: true,
        });
      }
      return;
    }
    await recordCaseActivity({ path: { case_id: caseId }, body, throwOnError: true });
  } catch (error) {
    logger.debug("[case] activity not recorded", { caseId, kind: body.kind, error });
  }
}
